import { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/table";
import { Heading } from "../components/heading";
import { Button } from "../components/button";
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { BSCustomer } from "../models/customer";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "../components/dialog";
import { Fieldset, Label } from "../components/fieldset";
import * as Headless from "@headlessui/react";
import { Input } from "../components/input";
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownMenu,
} from "../components/dropdown";
import { EllipsisHorizontalIcon } from "@heroicons/react/20/solid";
import EmptyState from "./components/empty-state";
import { PlusIcon } from "@heroicons/react/20/solid";
import { useTableSort } from '../hooks/useTableSort';
import { useColumnVisibility, ColumnConfig } from '../hooks/useColumnVisibility';
import { useTableSearch } from '../hooks/useTableSearch';
import { ChevronUpIcon, ChevronDownIcon, ChevronUpDownIcon, EyeIcon, EyeSlashIcon } from "@heroicons/react/20/solid";
import { Badge } from "../components/badge";
import { XMarkIcon } from "@heroicons/react/20/solid";

export default function Customers() {
  const collectionName = "customers";
  const emptyObject: BSCustomer = {
    id: '',
    name: "",
    emails: [],
    phone: "",
    riders: [],
  };

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [newObject, setNewObject] = useState<BSCustomer>(emptyObject);
  const [objects, setObjects] = useState<BSCustomer[]>([]);

  const columns: ColumnConfig<BSCustomer>[] = [
    { field: 'name', label: 'Name', defaultVisible: true },
    { field: 'emails', label: 'Email(s)', defaultVisible: true },
    { field: 'phone', label: 'Phone', defaultVisible: true },
    { field: 'riders', label: 'Riders', defaultVisible: true },
    { field: 'actions', label: 'Actions', defaultVisible: true }
  ];

  const { sortField, sortDirection, handleSort, getSortedData } = useTableSort<BSCustomer>('name');
  const { toggleColumn, isColumnVisible } = useColumnVisibility<BSCustomer>(columns);
  const { searchQuery, setSearchQuery, getFilteredData } = useTableSearch<BSCustomer>();

  useEffect(() => {
    const fetchData = () => {
      const q = query(collection(db, collectionName));
      onSnapshot(q, (querySnapshot) => {
        const objectsArray: BSCustomer[] = [];
        querySnapshot.forEach((doc) => {
          const serviceData = doc.data() as BSCustomer;
          objectsArray.push({ ...serviceData, id: doc.id });
        });
        setObjects(objectsArray);
      });
    };

    fetchData();
  }, []);

  const saveObject = async () => {
    if (newObject.id === "") {
      const newDocRef = doc(collection(db, collectionName));
      newObject.id = newDocRef.id;
      await setDoc(newDocRef, newObject);
    } else {
      await updateDoc(doc(db, collectionName, newObject.id), newObject);
    }

    setIsDialogOpen(false);
    resetForm();
  };

  const editObject = (object: BSCustomer) => {
    setNewObject(object);
    setIsDialogOpen(true);
  };

  const deleteObject = async (object: BSCustomer) => {
    await deleteDoc(doc(db, collectionName, object.id));
  };

  const resetForm = () => {
    setNewObject(emptyObject);
  };

  const emailStringToArray = (emailString: string): string[] => {
    return emailString.split(',')
      .map(email => email.trim())
      .filter(email => email !== '');
  };

  const emailArrayToString = (emails: string[]): string => {
    return emails.join(', ');
  };

  const getFilteredCustomers = () => {
    const filtered = getFilteredData(objects, (customer) => [
      customer.name || '',
      (customer.emails || []).join(', '),
      customer.phone || '',
      (customer.riders || []).join(', '),
    ]);

    return getSortedData(filtered);
  };

  const SortableHeader = ({ field, children }: { field: keyof BSCustomer, children: React.ReactNode }) => {
    const isCurrent = sortField === field;
    return (
      <TableHeader 
        className="cursor-pointer select-none group hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center gap-1">
          {children}
          <span className="inline-flex">
            {isCurrent ? (
              sortDirection === 'asc' ? (
                <ChevronUpIcon className="w-4 h-4" />
              ) : (
                <ChevronDownIcon className="w-4 h-4" />
              )
            ) : (
              <ChevronUpDownIcon className="w-4 h-4 opacity-0 group-hover:opacity-50" />
            )}
          </span>
        </div>
      </TableHeader>
    );
  };

  const addRider = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && e.currentTarget.value) {
      e.preventDefault();
      const newRider = e.currentTarget.value.trim();
      if (newRider && !newObject.riders?.includes(newRider)) {
        setNewObject({
          ...newObject,
          riders: [...(newObject.riders || []), newRider]
        });
        e.currentTarget.value = '';
      }
    }
  };

  const removeRider = (riderToRemove: string) => {
    setNewObject({
      ...newObject,
      riders: (newObject.riders || []).filter(rider => rider !== riderToRemove)
    });
  };

  return (
    <div>
      <div className="flex w-full flex-wrap items-end justify-between gap-4 border-b border-zinc-950/10 pb-6 dark:border-white/10">
        <Heading>Customers</Heading>
        <div className="flex items-center gap-4">
          <div className="relative">
            <Input
              type="search"
              placeholder="Search customers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-[250px]"
            />
          </div>
          <Dropdown>
            <DropdownButton>
              <EyeIcon className="w-5 h-5 mr-2" />
              Columns
            </DropdownButton>
            <DropdownMenu anchor="bottom end">
              {columns.map(({ field, label }) => (
                <DropdownItem
                  key={field}
                  onClick={() => toggleColumn(field)}
                >
                  <div className="flex items-center gap-2">
                    {isColumnVisible(field) ? (
                      <EyeIcon className="w-4 h-4" />
                    ) : (
                      <EyeSlashIcon className="w-4 h-4" />
                    )}
                    {label}
                  </div>
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
          <Button onClick={() => {
            resetForm();
            setIsDialogOpen(true);
          }}>Add Customer</Button>
        </div>
      </div>

      {objects.length > 0 ? (
        <Table
          dense
          striped
          className="[--gutter:theme(spacing.6)] sm:[--gutter:theme(spacing.8)]"
        >
          <TableHead>
            <TableRow>
              {columns.map(({ field, label }) => 
                isColumnVisible(field) && (
                  field === 'actions' ? (
                    <TableHeader key={field}>Actions</TableHeader>
                  ) : (
                    <SortableHeader key={field} field={field}>
                      {label}
                    </SortableHeader>
                  )
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {getFilteredCustomers().map((object) => (
              <TableRow key={object.id}>
                {isColumnVisible('name') && (
                  <TableCell className="font-medium">{object.name}</TableCell>
                )}
                {isColumnVisible('emails') && (
                  <TableCell>
                    {object.emails.length > 2 
                      ? `${object.emails.slice(0, 2).join(', ')}...`
                      : object.emails.join(', ')}
                  </TableCell>
                )}
                {isColumnVisible('phone') && (
                  <TableCell>{object.phone}</TableCell>
                )}
                {isColumnVisible('riders') && (
                  <TableCell>{object.riders?.length > 2 
                      ? `${object.riders?.slice(0, 2).join(', ')}...`
                      : object.riders?.join(', ')}</TableCell>
                )}
                {isColumnVisible('actions') && (
                  <TableCell>
                    <div className="-mx-3 -my-1.5 sm:-mx-2.5">
                      <Dropdown>
                        <DropdownButton plain aria-label="More options">
                          <EllipsisHorizontalIcon />
                        </DropdownButton>
                        <DropdownMenu anchor="bottom end">
                          <DropdownItem onClick={() => editObject(object)}>
                            Edit
                          </DropdownItem>
                          <DropdownDivider />
                          <DropdownItem onClick={() => deleteObject(object)}>
                            Delete
                          </DropdownItem>
                        </DropdownMenu>
                      </Dropdown>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <EmptyState
          title={"No Customers"}
          description={"Add new services to manage them here"}
        >
          <PlusIcon className="w-10 h-10" />
        </EmptyState>
      )}

      <Dialog open={isDialogOpen} onClose={setIsDialogOpen}>
        <DialogTitle>New Customer</DialogTitle>
        <DialogBody>
          <Fieldset>
            <div
              data-slot="control"
              className="grid grid-cols-1 items-center gap-x-4 gap-y-6 sm:grid-cols-3"
            >
              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>Name</Label>
                <Input
                  type="text"
                  onChange={(e) =>
                    setNewObject({
                      ...newObject,
                      name: e.target.value,
                    })
                  }
                  value={newObject.name}
                  className="mt-3 sm:col-span-2 sm:mt-0"
                  name="name"
                />
              </Headless.Field>
              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>Email(s)</Label>
                <Input
                  type="text"
                  onChange={(e) =>
                    setNewObject({
                      ...newObject,
                      emails: emailStringToArray(e.target.value),
                    })
                  }
                  value={emailArrayToString(newObject.emails)}
                  className="mt-3 sm:col-span-2 sm:mt-0"
                  name="email"
                  placeholder="email1@example.com, email2@example.com"
                />
              </Headless.Field>
              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>Phone</Label>
                <Input
                  type="phone"
                  onChange={(e) =>
                    setNewObject({
                      ...newObject,
                      phone: e.target.value,
                    })
                  }
                  value={newObject.phone}
                  className="mt-3 sm:col-span-2 sm:mt-0"
                  name="phone"
                />
              </Headless.Field>
              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>Riders</Label>
                <div className="mt-3 sm:col-span-2 sm:mt-0">
                  <div className="flex flex-wrap gap-2 mb-2">
                    {newObject.riders?.map(rider => (
                      <Badge 
                        key={rider} 
                        className="group"
                      >
                        {rider}
                        <button
                          type="button"
                          onClick={() => removeRider(rider)}
                          className="ml-1 group-hover:text-zinc-500 dark:group-hover:text-zinc-400"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                  <Input
                    type="text"
                    placeholder="Type a rider name and press Enter..."
                    onKeyDown={addRider}
                    className="mt-1"
                  />
                </div>
              </Headless.Field>
            </div>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setIsDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={() => saveObject()}>{ newObject.id === '' ? 'Add' : 'Save' }</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
